// src/features/reports/pages/ProfitLossReportPage.tsx
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getProfitLossReport, getExportUrl, downloadReportFile } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import {
    DollarSign, TrendingUp, TrendingDown, Percent,
    FileSpreadsheet, FileText, ArrowRight, PieChart,
    Building2, Scale, Layers, HelpCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

export function ProfitLossReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report-pandl', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-profit-loss', tenantId, params],
        queryFn: () => getProfitLossReport(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        downloadReportFile(tenantId, 'p_and_l', format, params as any);
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const summary = data?.summary || {};
    const revenueBreakdown = data?.revenueBreakdown || [];
    const expenseBreakdown = data?.expenseBreakdown || [];

    const grossSales = parseFloat(summary.grossSales || '0');
    const discounts = parseFloat(summary.discounts || '0');
    const refunds = parseFloat(summary.refunds || '0');
    const netSales = parseFloat(summary.netSales || '0');
    const cogs = parseFloat(summary.costOfGoodsSold || '0');
    const grossProfit = parseFloat(summary.grossProfit || '0');
    const totalExpenses = parseFloat(summary.totalOperatingExpenses || '0');
    const netOperatingProfit = parseFloat(summary.netOperatingProfit || '0');

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <ReportHeader
                title="Profit & Loss Statement (P&L)"
                description="Comprehensive income statement, COGS, and operating net margin"
                icon={DollarSign}
                iconGradient="from-emerald-600 to-teal-700"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
                        <Spinner size="lg" variant="emerald" />
                        <p className="text-xs text-slate-500 font-medium">Generating financial statement...</p>
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
                            <SummaryCard
                                label="Gross Revenue"
                                value={formatNaira(grossSales)}
                                icon={DollarSign}
                                gradient="from-blue-500 to-indigo-600"
                                subtitle={`${summary.completedOrders || 0} completed sales`}
                            />
                            <SummaryCard
                                label="Net Sales Revenue"
                                value={formatNaira(netSales)}
                                icon={TrendingUp}
                                gradient="from-teal-500 to-emerald-600"
                                subtitle="After discounts & returns"
                            />
                            <SummaryCard
                                label="Cost of Goods (COGS)"
                                value={formatNaira(cogs)}
                                icon={TrendingDown}
                                gradient="from-rose-500 to-red-600"
                                subtitle={`${netSales > 0 ? ((cogs / netSales) * 100).toFixed(1) : 0}% of net sales`}
                            />
                            <SummaryCard
                                label="Gross Operating Profit"
                                value={formatNaira(grossProfit)}
                                icon={Percent}
                                gradient="from-amber-500 to-orange-600"
                                subtitle={`${summary.grossMarginPct || 0}% gross margin`}
                            />
                            <SummaryCard
                                label="Operating Expenses"
                                value={formatNaira(totalExpenses)}
                                icon={Building2}
                                gradient="from-purple-500 to-violet-600"
                                subtitle={`${expenseBreakdown.length} expense categories`}
                            />
                            <SummaryCard
                                label="Net Income (EBITDA)"
                                value={formatNaira(netOperatingProfit)}
                                icon={Scale}
                                gradient={netOperatingProfit >= 0 ? "from-emerald-500 to-teal-700" : "from-rose-600 to-red-800"}
                                subtitle={`${summary.netProfitMarginPct || 0}% net margin`}
                            />
                        </div>

                        {/* Formal P&L Statement Card */}
                        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm overflow-hidden">
                            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                                        Income Statement Breakdown
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Standard GAAP-aligned financial reporting for management and tax compliance
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleExport('pdf')}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-50 shadow-xs transition-all"
                                    >
                                        <FileText className="w-3.5 h-3.5 text-rose-500" /> Export PDF
                                    </button>
                                    <button
                                        onClick={() => handleExport('excel')}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-50 shadow-xs transition-all"
                                    >
                                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Export Excel
                                    </button>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-bold uppercase text-[10px] tracking-wider">
                                            <th className="py-3 px-6 text-left">Financial Line Item</th>
                                            <th className="py-3 px-6 text-left">Classification</th>
                                            <th className="py-3 px-6 text-right">Amount (NGN)</th>
                                            <th className="py-3 px-6 text-right">% of Net Sales</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium">
                                        {/* Operating Revenue Section */}
                                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                                            <td colSpan={4} className="py-2.5 px-6 uppercase text-[10px] tracking-wider text-teal-800">
                                                1. Operating Revenue
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50/50">
                                            <td className="py-3 px-6 pl-10 text-slate-800">Gross Sales Volume</td>
                                            <td className="py-3 px-6 text-slate-500">Gross Revenue</td>
                                            <td className="py-3 px-6 text-right font-bold text-slate-900">{formatNaira(grossSales)}</td>
                                            <td className="py-3 px-6 text-right text-slate-400">—</td>
                                        </tr>
                                        <tr className="hover:bg-slate-50/50 text-rose-600">
                                            <td className="py-2.5 px-6 pl-10">Less: Sales Discounts & Promotions</td>
                                            <td className="py-2.5 px-6 text-slate-400">Revenue Deduction</td>
                                            <td className="py-2.5 px-6 text-right font-semibold">- {formatNaira(discounts)}</td>
                                            <td className="py-2.5 px-6 text-right text-slate-400">{netSales > 0 ? ((discounts / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>
                                        <tr className="hover:bg-slate-50/50 text-rose-600">
                                            <td className="py-2.5 px-6 pl-10">Less: Returns & Order Refunds</td>
                                            <td className="py-2.5 px-6 text-slate-400">Revenue Deduction</td>
                                            <td className="py-2.5 px-6 text-right font-semibold">- {formatNaira(refunds)}</td>
                                            <td className="py-2.5 px-6 text-right text-slate-400">{netSales > 0 ? ((refunds / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>
                                        <tr className="bg-emerald-50/50 font-bold text-emerald-950">
                                            <td className="py-3 px-6 pl-8">NET SALES REVENUE</td>
                                            <td className="py-3 px-6 text-emerald-700">Net Operating Revenue</td>
                                            <td className="py-3 px-6 text-right font-bold text-sm">{formatNaira(netSales)}</td>
                                            <td className="py-3 px-6 text-right font-bold">100.0%</td>
                                        </tr>

                                        {/* Cost of Goods Sold Section */}
                                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                                            <td colSpan={4} className="py-2.5 px-6 uppercase text-[10px] tracking-wider text-teal-800">
                                                2. Cost of Goods Sold (COGS)
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50/50">
                                            <td className="py-3 px-6 pl-10 text-slate-800">Direct Inventory & Recipe Cost</td>
                                            <td className="py-3 px-6 text-slate-500">Raw Material / Product Cost</td>
                                            <td className="py-3 px-6 text-right font-bold text-rose-600">- {formatNaira(cogs)}</td>
                                            <td className="py-3 px-6 text-right text-slate-600">{netSales > 0 ? ((cogs / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>
                                        <tr className="bg-amber-50/50 font-bold text-amber-950">
                                            <td className="py-3 px-6 pl-8">GROSS OPERATING PROFIT</td>
                                            <td className="py-3 px-6 text-amber-700">Gross Margin</td>
                                            <td className="py-3 px-6 text-right font-bold text-sm">{formatNaira(grossProfit)}</td>
                                            <td className="py-3 px-6 text-right font-bold">{summary.grossMarginPct || 0}%</td>
                                        </tr>

                                        {/* Operating Overhead Section */}
                                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                                            <td colSpan={4} className="py-2.5 px-6 uppercase text-[10px] tracking-wider text-teal-800">
                                                3. Operating Expenses & Overhead
                                            </td>
                                        </tr>
                                        {expenseBreakdown.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="py-3 px-6 pl-10 text-slate-400 italic">
                                                    No operational expenses recorded during this period
                                                </td>
                                            </tr>
                                        ) : (
                                            expenseBreakdown.map((exp: any) => (
                                                <tr key={exp.categoryName} className="hover:bg-slate-50/50">
                                                    <td className="py-2.5 px-6 pl-10 text-slate-700">{exp.categoryName}</td>
                                                    <td className="py-2.5 px-6 text-slate-400">{exp.expenseCount} payments</td>
                                                    <td className="py-2.5 px-6 text-right font-semibold text-rose-600">- {formatNaira(exp.amount)}</td>
                                                    <td className="py-2.5 px-6 text-right text-slate-500">{exp.expenseSharePct}%</td>
                                                </tr>
                                            ))
                                        )}
                                        <tr className="bg-rose-50/50 font-bold text-rose-950">
                                            <td className="py-3 px-6 pl-8">TOTAL OPERATING EXPENSES</td>
                                            <td className="py-3 px-6 text-rose-700">Total Overhead</td>
                                            <td className="py-3 px-6 text-right font-bold text-sm">- {formatNaira(totalExpenses)}</td>
                                            <td className="py-3 px-6 text-right font-bold">{netSales > 0 ? ((totalExpenses / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>

                                        {/* Net Operating Profit EBITDA */}
                                        <tr className="bg-gradient-to-r from-emerald-100/80 to-teal-100/80 font-bold text-slate-900 text-sm">
                                            <td className="py-4 px-6 uppercase tracking-wider text-emerald-950">
                                                NET OPERATING PROFIT (EBITDA)
                                            </td>
                                            <td className="py-4 px-6 text-emerald-800 text-xs font-bold">
                                                Bottom Line Net Profit
                                            </td>
                                            <td className="py-4 px-6 text-right text-base text-emerald-800">
                                                {formatNaira(netOperatingProfit)}
                                            </td>
                                            <td className="py-4 px-6 text-right text-emerald-800">
                                                {summary.netProfitMarginPct || 0}%
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Revenue & Expense Category Visual Breakdown */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Revenue by Category */}
                            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs">
                                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 mb-4">
                                    <Layers className="w-4 h-4 text-teal-600" /> Revenue Contribution by Product Category
                                </h3>
                                {revenueBreakdown.length === 0 ? (
                                    <p className="text-xs text-slate-400 italic">No category sales recorded</p>
                                ) : (
                                    <div className="space-y-3">
                                        {revenueBreakdown.slice(0, 6).map((cat: any) => (
                                            <div key={cat.category} className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="font-semibold text-slate-800">{cat.category}</span>
                                                    <span className="font-bold text-slate-900">{formatNaira(cat.grossSales)} ({cat.revenueSharePct}%)</span>
                                                </div>
                                                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                                    <div
                                                        className="bg-gradient-to-r from-teal-500 to-emerald-600 h-2 rounded-full"
                                                        style={{ width: `${Math.min(parseFloat(cat.revenueSharePct) || 0, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Operating Overhead by Category */}
                            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs">
                                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 mb-4">
                                    <Building2 className="w-4 h-4 text-purple-600" /> Operating Overhead Distribution
                                </h3>
                                {expenseBreakdown.length === 0 ? (
                                    <p className="text-xs text-slate-400 italic">No operating expenses recorded</p>
                                ) : (
                                    <div className="space-y-3">
                                        {expenseBreakdown.slice(0, 6).map((exp: any) => (
                                            <div key={exp.categoryName} className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="font-semibold text-slate-800">{exp.categoryName}</span>
                                                    <span className="font-bold text-rose-600">{formatNaira(exp.amount)} ({exp.expenseSharePct}%)</span>
                                                </div>
                                                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                                    <div
                                                        className="bg-gradient-to-r from-purple-500 to-pink-600 h-2 rounded-full"
                                                        style={{ width: `${Math.min(parseFloat(exp.expenseSharePct) || 0, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
